import { RichiestaModuloDTOPermessiItem } from '@framework/api-client/api/inquiries-compile/schemas/richiestaModuloDTOPermessiItem.ts';
import { useFillableSubmissionContext } from '@framework/common-web/engine/FillableSubmissionContext.tsx';
import {
  type BrandedHref,
  foreignRoute,
  useHrefGenerator,
} from '@framework/ui/core/router/TypedRoute.tsx';
import { useMemo } from 'react';
import { useDelegationContext } from '../../delegation/context/DelegationContext.tsx';
import { ModuleFormSource } from '../ModuleFormSource.ts';

/**
 * Hook che calcola l'href al modulo sorgente di una sezione condivisa esterna,
 * comprensivo del basename del router (es. `/frontoffice`).
 *
 * - Il link viene generato solo in contesto FRONTOFFICE.
 * - Se il delegation context è disponibile, verifica che l'utente abbia il
 *   permesso `COMPILAZIONE` sul modulo sorgente; in caso contrario il link
 *   viene nascosto.
 * - Se il delegation context è assente (es. richiesta inviata) il link viene
 *   comunque mostrato (l'utente ha accesso alla richiesta).
 */
export function useSharedSectionSourceHref(
  sourceModuleId: string | null | undefined,
  moduleFormSource: ModuleFormSource,
): BrandedHref | null {
  const { submissionId } = useFillableSubmissionContext();
  const delegationContext = useDelegationContext();
  const toHref = useHrefGenerator();

  return useMemo(() => {
    // Il link ha senso solo nelle pagine FO delle richieste
    if (moduleFormSource !== ModuleFormSource.FRONTOFFICE) return null;
    // Se non ho sourceModuleId o submissionId, non posso costruire l'href
    if (!(sourceModuleId && submissionId)) return null;

    // Se il delegation context è disponibile con dati, verifico i permessi
    // sul modulo sorgente. Senza permesso COMPILAZIONE il link non appare.
    if (delegationContext?.delegationData) {
      const sourceDelegation =
        delegationContext.delegationData.moduleDelegation.find(
          moduleDelegation =>
            moduleDelegation.submissionModule.id === sourceModuleId,
        );

      const canCompile = sourceDelegation?.submissionModule.permessi?.includes(
        RichiestaModuloDTOPermessiItem.COMPILAZIONE,
      );

      if (!canCompile) return null;
    }

    // Route di frontoffice-web: il package è compilato anche da app che non
    // la registrano (es. admin), quindi va marcata come esterna.
    return toHref(foreignRoute('/richieste/:id/moduli/:moduleId'), {
      id: submissionId,
      moduleId: sourceModuleId,
    });
  }, [
    sourceModuleId,
    submissionId,
    delegationContext,
    moduleFormSource,
    toHref,
  ]);
}
